import { useState, useEffect } from "react";
import { usePublicAdmin } from "@/contexts/PublicAdminAuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, UserCheck, UserX, TrendingUp, Shield, Loader2 } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const AMBER = "#f39c12";
const CHART_COLORS = ["#f39c12", "#3b82f6", "#10b981", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#eab308", "#64748b", "#f97316", "#14b8a6"];
const cardStyle = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20 };
const tooltipStyle = { backgroundColor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: '#f8fafc', fontSize: 12 };

export default function PublicAdminDashboard() {
  const { authHeader, admin } = usePublicAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    show_sml: false,
    show_cat_rank: false,
    show_predictor: false,
    show_state_quota: true,
    show_aiims: true,
    show_aiq_15: true,
    show_deemed: true,
    show_other_states: true,
    predictor_show_sml: true,
    predictor_show_cat_rank: true,
    col_show_category: true,
    col_show_cutoff: true,
    col_show_your_rank: true,
    col_show_margin: true,
    col_show_buffer: true,
    allow_pdf_download: true,
    col_show_chance: true,
    form_state_active: true,
    form_mcc_active: true
  });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get("/public-admin/dashboard", authHeader());
        setData(res.data);
        try {
          const setRes = await api.get("/public/settings");
          setSettings({
            show_sml: !!setRes.data?.show_sml,
            show_cat_rank: !!setRes.data?.show_cat_rank,
            show_predictor: !!setRes.data?.show_predictor,
            show_state_quota: setRes.data?.show_state_quota !== false,
            show_aiims: setRes.data?.show_aiims !== false,
            show_aiq_15: setRes.data?.show_aiq_15 !== false,
            show_deemed: setRes.data?.show_deemed !== false,
            show_other_states: setRes.data?.show_other_states !== false,
            predictor_show_sml: setRes.data?.predictor_show_sml !== false,
            predictor_show_cat_rank: setRes.data?.predictor_show_cat_rank !== false,
            col_show_category: setRes.data?.col_show_category !== false,
            col_show_cutoff: setRes.data?.col_show_cutoff !== false,
            col_show_your_rank: setRes.data?.col_show_your_rank !== false,
            col_show_margin: setRes.data?.col_show_margin !== false,
            col_show_buffer: setRes.data?.col_show_buffer !== false,
            allow_pdf_download: setRes.data?.allow_pdf_download !== false,
            col_show_chance: setRes.data?.col_show_chance !== false,
            form_state_active: setRes.data?.form_state_active !== false,
            form_mcc_active: setRes.data?.form_mcc_active !== false
          });
        } catch (e) {
          console.error("Failed to fetch settings", e);
        }
      } catch (err) {
        toast.error("Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);// eslint-disable-line

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader2 className="h-8 w-8 animate-spin" style={{ color: AMBER }} />
    </div>
  );

  if (!data) return null;

  const genderPie = Object.entries(data.gender_distribution || {}).map(([k, v]) => ({ name: k, value: v }));
  const categoryPie = Object.entries(data.category_distribution || {}).map(([k, v]) => ({ name: k, value: v }));
  const districtBar = Object.entries(data.district_distribution || {}).map(([k, v]) => ({ name: k, value: v }));
  const scoreBar = (data.score_distribution || []).map(d => ({
    name: d.range < 200 ? "0-200" : d.range < 300 ? "200-300" : d.range < 400 ? "300-400" : d.range < 500 ? "400-500" : d.range < 600 ? "500-600" : d.range < 700 ? "600-700" : "700+",
    count: d.count
  }));

  const StatCard = ({ icon: Icon, label, value, color, sub }) => (
    <div className="p-5 flex items-start gap-4 group hover:scale-[1.02] transition-transform" style={cardStyle}>
      <div className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
        <Icon className="h-6 w-6" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: '#64748b' }}>{label}</p>
        <p className="text-3xl font-black mt-1 leading-none" style={{ color: '#f8fafc' }}>{value}</p>
        {sub && <p className="text-xs mt-1 font-medium" style={{ color: '#64748b' }}>{sub}</p>}
      </div>
    </div>
  );

  const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="#f8fafc" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={700}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const handleToggleSetting = async (key) => {
    const newVal = !settings[key];
    setSavingSettings(true);
    try {
      await api.put("/public-admin/settings", { ...settings, [key]: newVal }, authHeader());
      setSettings(prev => ({ ...prev, [key]: newVal }));
      toast.success("Settings updated successfully");
    } catch (err) {
      toast.error("Failed to update settings");
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl md:text-3xl font-black tracking-tight" style={{ color: '#f8fafc' }}>Dashboard</h1>
        <p className="text-sm mt-1" style={{ color: '#64748b' }}>Public Student Portal Analytics</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Students" value={data.total_users} color="#f39c12" />
        <StatCard icon={UserCheck} label="Active" value={data.active_users} color="#10b981" />
        <StatCard icon={UserX} label="Disabled" value={data.disabled_users} color="#ef4444" />
        <StatCard icon={TrendingUp} label="Last 7 Days" value={data.recent_registrations} color="#3b82f6" sub="New registrations" />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gender Pie */}
        <div className="p-6" style={cardStyle}>
          <h3 className="font-bold text-sm uppercase tracking-wider mb-4" style={{ color: '#94a3b8' }}>Gender Distribution</h3>
          {genderPie.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={genderPie} cx="50%" cy="50%" outerRadius={100} innerRadius={50} dataKey="value" labelLine={false} label={CustomPieLabel}>
                  {genderPie.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12, color: '#94a3b8' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-center py-16" style={{ color: '#64748b' }}>No data yet</p>}
        </div>

        {/* Category Pie */}
        <div className="p-6" style={cardStyle}>
          <h3 className="font-bold text-sm uppercase tracking-wider mb-4" style={{ color: '#94a3b8' }}>Category Distribution</h3>
          {categoryPie.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={categoryPie} cx="50%" cy="50%" outerRadius={100} innerRadius={50} dataKey="value" labelLine={false} label={CustomPieLabel}>
                  {categoryPie.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-center py-16" style={{ color: '#64748b' }}>No data yet</p>}
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* NEET Score Bar */}
        <div className="p-6" style={cardStyle}>
          <h3 className="font-bold text-sm uppercase tracking-wider mb-4" style={{ color: '#94a3b8' }}>NEET Score Distribution</h3>
          {scoreBar.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={scoreBar}>
                <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {scoreBar.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-center py-16" style={{ color: '#64748b' }}>No data yet</p>}
        </div>

        {/* District Bar */}
        <div className="p-6" style={cardStyle}>
          <h3 className="font-bold text-sm uppercase tracking-wider mb-4" style={{ color: '#94a3b8' }}>Top Districts</h3>
          {districtBar.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={districtBar} layout="vertical">
                <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis dataKey="name" type="category" tick={{ fill: '#94a3b8', fontSize: 10 }} width={120} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]} fill="#f39c12" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-center py-16" style={{ color: '#64748b' }}>No data yet</p>}
        </div>
      </div>

      {/* Quick Info & Global Settings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-6" style={cardStyle}>
          <h3 className="font-bold text-sm uppercase tracking-wider mb-4" style={{ color: '#94a3b8' }}>Quick Info</h3>
          <div className="flex items-center gap-4">
            <Shield className="h-8 w-8" style={{ color: '#3b82f6' }} />
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Sub-Admins</p>
              <p className="text-2xl font-black" style={{ color: '#f8fafc' }}>{data.sub_admin_count}</p>
            </div>
          </div>
        </div>

        {admin?.role === "PUBLIC_ADMIN" && (
          <div className="p-6" style={cardStyle}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm uppercase tracking-wider" style={{ color: '#94a3b8' }}>Global Settings</h3>
              {savingSettings && <Loader2 className="h-4 w-4 animate-spin text-amber-500" />}
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-200">Show SML Column</p>
                  <p className="text-xs text-slate-400">Display State Merit List in public cutoffs</p>
                </div>
                <button 
                  onClick={() => handleToggleSetting('show_sml')}
                  disabled={savingSettings}
                  className={`w-12 h-6 rounded-full transition-colors relative flex items-center ${settings.show_sml ? 'bg-amber-500' : 'bg-slate-700'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white absolute transition-transform ${settings.show_sml ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-200">Show Category Rank Column</p>
                  <p className="text-xs text-slate-400">Display Category Rank in public cutoffs</p>
                </div>
                <button 
                  onClick={() => handleToggleSetting('show_cat_rank')}
                  disabled={savingSettings}
                  className={`w-12 h-6 rounded-full transition-colors relative flex items-center ${settings.show_cat_rank ? 'bg-amber-500' : 'bg-slate-700'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white absolute transition-transform ${settings.show_cat_rank ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-200">Enable AI College Predictor</p>
                  <p className="text-xs text-slate-400">Turn on the predictor tool visibility for public users</p>
                </div>
                <button 
                  onClick={() => handleToggleSetting('show_predictor')}
                  disabled={savingSettings}
                  className={`w-12 h-6 rounded-full transition-colors relative flex items-center ${settings.show_predictor ? 'bg-amber-500' : 'bg-slate-700'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white absolute transition-transform ${settings.show_predictor ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              <div className="pt-2 border-t border-slate-700/40 space-y-4">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Registration Forms Control</p>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-200">State Quota Details Form</p>
                    <p className="text-xs text-slate-400">Allow public users to fill state quota registration details</p>
                  </div>
                  <button 
                    onClick={() => handleToggleSetting('form_state_active')}
                    disabled={savingSettings}
                    className={`w-12 h-6 rounded-full transition-colors relative flex items-center ${settings.form_state_active ? 'bg-amber-500' : 'bg-slate-700'}`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white absolute transition-transform ${settings.form_state_active ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-200">MBBS/BDS MCC Details Form</p>
                    <p className="text-xs text-slate-400">Allow public users to fill MCC institutional quota registration details</p>
                  </div>
                  <button 
                    onClick={() => handleToggleSetting('form_mcc_active')}
                    disabled={savingSettings}
                    className={`w-12 h-6 rounded-full transition-colors relative flex items-center ${settings.form_mcc_active ? 'bg-amber-500' : 'bg-slate-700'}`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white absolute transition-transform ${settings.form_mcc_active ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>

              {settings.show_predictor && (
                <div className="pl-6 border-l-2 border-slate-700/60 space-y-4 mt-2">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold text-slate-300">State Quota (Maharashtra 85%)</p>
                        <p className="text-[10px] text-slate-400">Display State Quota selection tab</p>
                      </div>
                      <button 
                        onClick={() => handleToggleSetting('show_state_quota')}
                        disabled={savingSettings}
                        className={`w-10 h-5 rounded-full transition-colors relative flex items-center ${settings.show_state_quota ? 'bg-amber-500' : 'bg-slate-700'}`}
                      >
                        <div className={`w-4 h-4 rounded-full bg-white absolute transition-transform ${settings.show_state_quota ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                    {settings.show_state_quota && (
                      <div className="pl-6 border-l border-slate-700/40 space-y-3 mt-1 mb-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[11px] font-bold text-slate-400">Display State Merit List (SML)</p>
                            <p className="text-[9px] text-slate-500">Collect SML rank input field</p>
                          </div>
                          <button 
                            onClick={() => handleToggleSetting('predictor_show_sml')}
                            disabled={savingSettings}
                            className={`w-8 h-4 rounded-full transition-colors relative flex items-center ${settings.predictor_show_sml ? 'bg-amber-500' : 'bg-slate-700'}`}
                          >
                            <div className={`w-3.5 h-3.5 rounded-full bg-white absolute transition-transform ${settings.predictor_show_sml ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[11px] font-bold text-slate-400">Display Category Rank</p>
                            <p className="text-[9px] text-slate-500">Collect Category Rank input field</p>
                          </div>
                          <button 
                            onClick={() => handleToggleSetting('predictor_show_cat_rank')}
                            disabled={savingSettings}
                            className={`w-8 h-4 rounded-full transition-colors relative flex items-center ${settings.predictor_show_cat_rank ? 'bg-amber-500' : 'bg-slate-700'}`}
                          >
                            <div className={`w-3.5 h-3.5 rounded-full bg-white absolute transition-transform ${settings.predictor_show_cat_rank ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold text-slate-300">AIIMS (All India)</p>
                      <p className="text-[10px] text-slate-400">Display AIIMS selection tab</p>
                    </div>
                    <button 
                      onClick={() => handleToggleSetting('show_aiims')}
                      disabled={savingSettings}
                      className={`w-10 h-5 rounded-full transition-colors relative flex items-center ${settings.show_aiims ? 'bg-amber-500' : 'bg-slate-700'}`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white absolute transition-transform ${settings.show_aiims ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold text-slate-300">AIQ 15% (All India)</p>
                      <p className="text-[10px] text-slate-400">Display AIQ 15% selection tab</p>
                    </div>
                    <button 
                      onClick={() => handleToggleSetting('show_aiq_15')}
                      disabled={savingSettings}
                      className={`w-10 h-5 rounded-full transition-colors relative flex items-center ${settings.show_aiq_15 ? 'bg-amber-500' : 'bg-slate-700'}`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white absolute transition-transform ${settings.show_aiq_15 ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold text-slate-300">Deemed Universities</p>
                      <p className="text-[10px] text-slate-400">Display Deemed selection tab</p>
                    </div>
                    <button 
                      onClick={() => handleToggleSetting('show_deemed')}
                      disabled={savingSettings}
                      className={`w-10 h-5 rounded-full transition-colors relative flex items-center ${settings.show_deemed ? 'bg-amber-500' : 'bg-slate-700'}`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white absolute transition-transform ${settings.show_deemed ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold text-slate-300">Other States Quota</p>
                      <p className="text-[10px] text-slate-400">Display Other States selection tab</p>
                    </div>
                    <button 
                      onClick={() => handleToggleSetting('show_other_states')}
                      disabled={savingSettings}
                      className={`w-10 h-5 rounded-full transition-colors relative flex items-center ${settings.show_other_states ? 'bg-amber-500' : 'bg-slate-700'}`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white absolute transition-transform ${settings.show_other_states ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>

                  {/* Column Display Section */}
                  <div className="pt-2 border-t border-slate-700/40 space-y-3">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Prediction Table Columns Display</p>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-slate-300">Category Column</p>
                        <p className="text-[10px] text-slate-400">Display Category of cutoffs in table</p>
                      </div>
                      <button 
                        onClick={() => handleToggleSetting('col_show_category')}
                        disabled={savingSettings}
                        className={`w-8 h-4 rounded-full transition-colors relative flex items-center ${settings.col_show_category ? 'bg-amber-500' : 'bg-slate-700'}`}
                      >
                        <div className={`w-3.5 h-3.5 rounded-full bg-white absolute transition-transform ${settings.col_show_category ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-slate-300">Cutoff Column</p>
                        <p className="text-[10px] text-slate-400">Display Cutoff ranks in table</p>
                      </div>
                      <button 
                        onClick={() => handleToggleSetting('col_show_cutoff')}
                        disabled={savingSettings}
                        className={`w-8 h-4 rounded-full transition-colors relative flex items-center ${settings.col_show_cutoff ? 'bg-amber-500' : 'bg-slate-700'}`}
                      >
                        <div className={`w-3.5 h-3.5 rounded-full bg-white absolute transition-transform ${settings.col_show_cutoff ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-slate-300">Your Rank Column</p>
                        <p className="text-[10px] text-slate-400">Display Student's rank in table</p>
                      </div>
                      <button 
                        onClick={() => handleToggleSetting('col_show_your_rank')}
                        disabled={savingSettings}
                        className={`w-8 h-4 rounded-full transition-colors relative flex items-center ${settings.col_show_your_rank ? 'bg-amber-500' : 'bg-slate-700'}`}
                      >
                        <div className={`w-3.5 h-3.5 rounded-full bg-white absolute transition-transform ${settings.col_show_your_rank ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-slate-300">Margin Column</p>
                        <p className="text-[10px] text-slate-400">Display computed margins in table</p>
                      </div>
                      <button 
                        onClick={() => handleToggleSetting('col_show_margin')}
                        disabled={savingSettings}
                        className={`w-8 h-4 rounded-full transition-colors relative flex items-center ${settings.col_show_margin ? 'bg-amber-500' : 'bg-slate-700'}`}
                      >
                        <div className={`w-3.5 h-3.5 rounded-full bg-white absolute transition-transform ${settings.col_show_margin ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-slate-300">Buffer Column</p>
                        <p className="text-[10px] text-slate-400">Display rank buffer in table</p>
                      </div>
                      <button 
                        onClick={() => handleToggleSetting('col_show_buffer')}
                        disabled={savingSettings}
                        className={`w-8 h-4 rounded-full transition-colors relative flex items-center ${settings.col_show_buffer ? 'bg-amber-500' : 'bg-slate-700'}`}
                      >
                        <div className={`w-3.5 h-3.5 rounded-full bg-white absolute transition-transform ${settings.col_show_buffer ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-slate-300">Allow PDF Download</p>
                        <p className="text-[10px] text-slate-400">Enable PDF download button for students</p>
                      </div>
                      <button 
                        onClick={() => handleToggleSetting('allow_pdf_download')}
                        disabled={savingSettings}
                        className={`w-8 h-4 rounded-full transition-colors relative flex items-center ${settings.allow_pdf_download ? 'bg-amber-500' : 'bg-slate-700'}`}
                      >
                        <div className={`w-3.5 h-3.5 rounded-full bg-white absolute transition-transform ${settings.allow_pdf_download ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-slate-300">Chance Column</p>
                        <p className="text-[10px] text-slate-400">Display Chance badge and row colors in table</p>
                      </div>
                      <button 
                        onClick={() => handleToggleSetting('col_show_chance')}
                        disabled={savingSettings}
                        className={`w-8 h-4 rounded-full transition-colors relative flex items-center ${settings.col_show_chance ? 'bg-amber-500' : 'bg-slate-700'}`}
                      >
                        <div className={`w-3.5 h-3.5 rounded-full bg-white absolute transition-transform ${settings.col_show_chance ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
